import React from 'react';

import DataTable from '../shared/data_table';
import StatusPill from '../shared/status_pill';
import { SelectFilter } from '../shared/controls';
import { Ledger, Note, StatusCell, What, Who } from '../shared/cells';
import { fmt_count } from '../format';
import { MODE_REOPENED_NOW } from './params';

/*
 * the Close versions list: one row per teacher-month, net only (before, after, difference).
 * Props-only. rows: adapters.row_view; recalc: {teacher_uuid: {busy, text, failed}}.
 */
// Every SORT_KEYS key in both directions, worded as the order Ops see (not asc/desc).
const SORT_OPTIONS = [
  ['abs_net_diff', 'desc', 'Biggest difference first'],
  ['abs_net_diff', 'asc', 'Smallest difference first'],
  ['net_diff', 'desc', 'Largest increase first'],
  ['net_diff', 'asc', 'Largest decrease first'],
  ['teacher_name', 'asc', 'Teacher A–Z'],
  ['teacher_name', 'desc', 'Teacher Z–A'],
  ['baseline_net', 'desc', 'Highest before first'],
  ['baseline_net', 'asc', 'Lowest before first'],
  ['current_net', 'desc', 'Highest after first'],
  ['current_net', 'asc', 'Lowest after first'],
  ['version_count', 'desc', 'Most versions first'],
  ['version_count', 'asc', 'Fewest versions first'],
  ['changed_at', 'desc', 'Latest change first'],
  ['changed_at', 'asc', 'Earliest change first'],
  ['status', 'asc', 'Status A–Z'],
  ['status', 'desc', 'Status Z–A'],
  ['reopen_reason', 'asc', 'Reopen reason A–Z'],
  ['reopen_reason', 'desc', 'Reopen reason Z–A']
];

const DIFF_TONE = { pos: 'pos', neg: 'neg', muted: 'zero', warn: 'warn' };

// The card view has no headers, so phones sort with this select.
export const SortSelect = ({ sort, on_change }) => (
  <SelectFilter
    id="tp-cv-sort"
    label="Sort"
    aria_label="Sort by"
    value={`${sort.key}:${sort.dir}`}
    options={SORT_OPTIONS.map(([k, d, label]) => ({ value: `${k}:${d}`, label }))}
    active={false}
    onChange={(v) => {
      const [key, dir] = v.split(':');
      on_change(key, dir);
    }}
  />
);

// The whole reason, wrapped (never truncated).
const ReasonCell = ({ reason }) => <span className="tp-cv-reason">{reason}</span>;

const RecalcActions = ({ row, state, on_recalc }) => {
  if (!row.pending_recompute && !(state && state.text)) return null;
  const busy = !!(state && state.busy);
  return (
    <div className="tp-cv-recalc">
      {row.pending_recompute ? (
        <span className="tp-c-acts">
          <button
            type="button"
            className="tp-link"
            aria-disabled={busy ? 'true' : undefined}
            aria-label={`Recalculate ${row.teacher_name}`}
            onClick={() => (busy ? undefined : on_recalc(row))}
          >
            {busy ? 'Recalculating…' : 'Recalculate'}
          </button>
        </span>
      ) : null}
      {state && state.text ? (
        <span className={state.failed ? 'tp-neg' : 'tp-hint'} role="status">
          {state.text}
        </span>
      ) : null}
    </div>
  );
};

const side_item = (word, side) => ({
  label: side ? `${word} ${side.label}` : word,
  value: side ? side.amount_text : '—',
  tone: side ? '' : 'zero'
});

// Four tracks (list-row pattern): teacher · change · money ledger · status; the reopen reason is a
// note under the row. The sort select (Sort by) offers every order, headers the common ones.
const VersionsTable = ({ mode, rows, sort, on_sort, on_open, recalc, on_recalc }) => {
  const reopened = mode === MODE_REOPENED_NOW;
  const status_chip = (r) => <StatusPill tone={r.status.tone}>{r.status.label}</StatusPill>;
  const pending_line = (r) =>
    reopened && r.pending_recompute ? (
      // A failure is a short label here; its sentence is the row's note line.
      <span className={r.pending_failed ? 'tp-neg tp-cv-failed' : undefined}>
        {r.pending_failed ? 'Recalculation failed' : r.pending_text}
      </span>
    ) : null;
  const recalc_actions = (r) =>
    reopened ? (
      <RecalcActions row={r} state={recalc[r.teacher_uuid]} on_recalc={on_recalc} />
    ) : null;
  const columns = [
    {
      key: 'teacher',
      role: 'identifier',
      label: 'Teacher',
      sort_key: 'teacher_name',
      track: { compact: '27%', full: '25%' },
      render: (r) => (
        <Who
          name={
            <button
              type="button"
              className="tp-link tp-cv-open"
              aria-label={`Open versions for ${r.teacher_name}`}
              onClick={() => on_open(r)}
            >
              {r.teacher_name}
            </button>
          }
          meta={[
            r.teacher_slug,
            `${fmt_count(r.version_count)} ${r.version_count === 1 ? 'version' : 'versions'}`
          ]}
        />
      )
    },
    {
      key: 'change',
      label: 'Change',
      sort_key: 'changed_at',
      render: (r) => (
        <What
          title={
            r.before && r.after
              ? `${r.before.label} → ${r.after.label}`
              : (r.after || r.before || {}).label
          }
          lines={[
            `${r.changed_at}${r.changed_by ? ` · by ${r.changed_by}` : ''}`,
            r.before && r.before.source_note ? `${r.before.label}: ${r.before.source_note}` : null,
            r.after && r.after.source_note ? `${r.after.label}: ${r.after.source_note}` : null
          ]}
        />
      )
    },
    {
      key: 'diff',
      role: 'amount',
      label: 'Difference',
      align: 'right',
      sort_key: 'abs_net_diff',
      track: { compact: '168px', full: '196px' },
      render: (r) => (
        <Ledger
          items={[side_item('Before', r.before), side_item('After', r.after)]}
          total={{
            label: 'Difference',
            // "No change to payable" (components moved, payable did not) reads in the warning tone.
            value: r.diff.text,
            tone: DIFF_TONE[r.diff.tone]
          }}
        />
      )
    },
    {
      key: 'status',
      role: 'status',
      label: 'Status',
      sort_key: 'status',
      track: { compact: '150px', full: '176px' },
      render: (r) => (
        <StatusCell chip={status_chip(r)} meta={[pending_line(r)]} actions={recalc_actions(r)} />
      ),
      // A card's top-right corner holds the chip alone; the pending note and Recalculate go below.
      card_render: status_chip
    },
    {
      key: 'actions',
      role: 'actions',
      label: 'Recalculate',
      in_table: false,
      card_render: (r) =>
        pending_line(r) || recalc_actions(r) ? (
          <div className="tp-cv-card-recalc">
            {pending_line(r)}
            {recalc_actions(r)}
          </div>
        ) : null
    },
    {
      key: 'recalc_error',
      label: 'Recalculation error',
      line: { from: 'change', bare: true },
      render: (r) =>
        reopened && r.pending_recompute && r.pending_failed ? (
          <Note warn text={r.pending_text} />
        ) : null
    },
    {
      key: 'reopen_reason',
      label: 'Reopen reason',
      line: { from: 'change', bare: true },
      render: (r) =>
        r.reopen_reason ? (
          <Note label="Reopen reason" text={<ReasonCell reason={r.reopen_reason} />} />
        ) : null
    }
  ];
  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.key}
      sort={sort}
      on_sort={on_sort}
      onRowClick={on_open}
      card_below={660}
    />
  );
};

export default VersionsTable;
